import React, { useState, useEffect, useMemo } from 'react';
import { X, AlertTriangle, Info, Search, Eye } from 'lucide-react';
import { useUpdateRole, useDeleteRole, type Role, type ObjectPermission, getDefaultSearchPermissions, useAvailableObjects } from '../../../services/queries/roleQueries';
import { useRoles } from '../../../services/queries/roleQueries';
import { useRecentItems } from '../../../hooks/useRecentItems';
import { useSetupChanges } from '../../../hooks/useSetupChanges';
import { useToast } from '../../../contexts/ToastContext';
import { StandardButton, StandardInput, StandardSelect, StandardTextarea, StandardCheckbox } from '../../standard';
import { RolePermissionPreview } from './RolePermissionPreview';

interface EditRoleModalProps {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
}

export const EditRoleModal: React.FC<EditRoleModalProps> = ({ role, isOpen, onClose }) => {
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    parentRoleId: string;
    permissions: ObjectPermission[];
    searchPermissions: Role['searchPermissions'];
  }>({
    name: '',
    description: '',
    parentRoleId: '',
    permissions: [],
    searchPermissions: getDefaultSearchPermissions('Standard User')
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const updateRoleMutation = useUpdateRole();
  const deleteRoleMutation = useDeleteRole();
  const { data: existingRoles } = useRoles({ includeSystem: true });
  const { data: availableObjectsList } = useAvailableObjects();
  const { addRecentItem } = useRecentItems();
  const { logChange } = useSetupChanges();
  const { success, error } = useToast();

  // Memoize parent role options for StandardSelect
  const parentRoleOptions = useMemo(() => {
    const options = [{ value: '', label: 'None - This is a top-level role' }];
    if (existingRoles && role) {
      existingRoles
        .filter(r => r.id !== role.id)
        .forEach(r => options.push({ value: r.id, label: r.name }));
    }
    return options;
  }, [existingRoles, role]);
  const previewRole = useMemo(() => {
    if (!role) return null;
    return {
      ...role,
      name: formData.name || role.name,
      description: formData.description,
      parentRoleId: formData.parentRoleId || undefined,
      permissions: formData.permissions,
      searchPermissions: formData.searchPermissions,
      lastModified: new Date().toISOString(),
    };
  }, [role, formData]);

  // Track role edit in Recent Items
  useEffect(() => {
    if (role && isOpen) {
      addRecentItem({
        id: `role-${role.id}`,
        title: role.name,
        subtitle: 'Role & Permissions',
        path: '/admin?section=roles',
        type: 'setting',
        icon: 'Settings'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, isOpen]);

  useEffect(() => {
    if (role && availableObjectsList) {
      // Merge existing permissions with all available objects
      // This ensures we show ALL objects, not just ones with existing permissions
      const existingPermMap = new Map(
        role.permissions.map((p: ObjectPermission) => [p.objectId, p] as const)
      );
      
      const mergedPermissions = availableObjectsList.map(obj => {
        const existing = existingPermMap.get(obj.id);
        if (existing) {
          return { ...existing, objectName: obj.name }; // Use current name from objects list
        }
        return {
          objectId: obj.id,
          objectName: obj.name,
          read: false,
          write: false,
          delete: false
        };
      });

      setFormData({
        name: role.name,
        description: role.description || '',
        parentRoleId: role.parentRoleId || '',
        permissions: mergedPermissions,
        searchPermissions: role.searchPermissions || getDefaultSearchPermissions(role.name)
      });
    }
  }, [role, availableObjectsList]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!role) return;

    // Validate role name
    if (!formData.name || formData.name.trim() === '') {
      error('Role name is required', 'Please enter a name for this role');
      return;
    }

    // Validate role name is not just whitespace
    if (formData.name.trim().length === 0) {
      error('Invalid role name', 'Role name cannot be only spaces');
      return;
    }

    // Validate minimum length
    if (formData.name.trim().length < 2) {
      error('Role name too short', 'Role name must be at least 2 characters');
      return;
    }

    try {
      // Find parent role name if selected
      let parentRoleName: string | undefined;
      if (formData.parentRoleId) {
        const parentRole = existingRoles?.find(r => r.id === formData.parentRoleId);
        parentRoleName = parentRole?.name;
      }

      console.log('[EditRoleModal] Saving with permissions:', formData.permissions);
      console.log('[EditRoleModal] Saving with searchPermissions:', formData.searchPermissions);

      const updatePayload = {
        roleId: role.id,
        updates: {
          name: formData.name.trim(),
          description: formData.description,
          parentRoleId: formData.parentRoleId || undefined,
          parentRoleName,
          permissions: formData.permissions,
          searchPermissions: formData.searchPermissions
        }
      };

      const updatedRole = await updateRoleMutation.mutateAsync(updatePayload);
      console.log('[EditRoleModal] Save complete, returned role:', {
        permissions: updatedRole.permissions,
        searchPermissions: updatedRole.searchPermissions
      });

      // Log the change to setup changes
      logChange({
        action: 'updated',
        entityType: 'role',
        entityName: 'Role',
        targetName: updatedRole.name,
        description: `updated role "${updatedRole.name}"`,
        performedBy: 'Current User'
      });

      success('Role saved successfully!');
      onClose();
    } catch (err: any) {
      error('Failed to save role', err.message || 'Please try again or contact support');
    }
  };

  const handleDelete = async () => {
    if (!role) return;

    try {
      const roleName = role.name; // Store name before deletion
      await deleteRoleMutation.mutateAsync(role.id);

      // Log the change to setup changes
      logChange({
        action: 'deleted',
        entityType: 'role',
        entityName: 'Role',
        targetName: roleName,
        description: `deleted role "${roleName}"`,
        performedBy: 'Current User'
      });

      success('Role deleted successfully!');
      onClose();
      setShowDeleteConfirm(false);
    } catch (err: any) {
      error('Failed to delete role', err.message || 'Please try again or contact support');
    }
  };

  const updatePermission = (objectId: string, field: 'read' | 'write' | 'delete', value: boolean) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.map(p => {
        if (p.objectId === objectId) {
          const updated = { ...p, [field]: value };

          // Auto-enable read when write or delete is enabled
          if (field === 'write' && value) {
            updated.read = true;
          }
          if (field === 'delete' && value) {
            updated.read = true;
            updated.write = true;
          }

          // Auto-disable write/delete when read is disabled
          if (field === 'read' && !value) {
            updated.write = false;
            updated.delete = false;
          }

          // Auto-disable delete when write is disabled
          if (field === 'write' && !value) {
            updated.delete = false;
          }

          return updated;
        }
        return p;
      })
    }));
  };

  // Helper to update search permissions
  const updateSearchPermission = (field: keyof NonNullable<Role['searchPermissions']>, value: boolean) => {
    setFormData(prev => {
      if (!prev.searchPermissions) return prev;

      const updated = { ...prev.searchPermissions, [field]: value };

      // Auto-disable security permissions when system settings is disabled
      if (field === 'canSearchSystemSettings' && !value) {
        updated.canSearchUsers = false;
        updated.canSearchRoles = false;
        updated.canSearchSecurity = false;
      }

      // Update visibleCategories based on enabled permissions
      const newCategories: Array<'objects' | 'workflows' | 'fields' | 'settings'> = [];
      if (updated.canSearchObjects) newCategories.push('objects');
      if (updated.canSearchFields) newCategories.push('fields');
      if (updated.canSearchWorkflows) newCategories.push('workflows');
      if (updated.canSearchSystemSettings) newCategories.push('settings');
      updated.visibleCategories = newCategories;

      return {
        ...prev,
        searchPermissions: updated
      };
    });
  };

  if (!isOpen || !role) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Role</h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {role.name}
              {role.isSystem && <span className="ml-2 text-yellow-600">(System Role - Limited editing)</span>}
            </p>
          </div>
          <StandardButton
            variant="ghost"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </StandardButton>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {role.isSystem && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  <strong>System Role:</strong> This is a system role. You can modify permissions but cannot delete it or change its name.
                </p>
              </div>
            )}

            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Basic Information</h3>

              <div className="space-y-4">
                <StandardInput
                  label="Role Name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  disabled={role.isSystem}
                />

                <StandardTextarea
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                />

                <StandardSelect
                  label="Parent Role"
                  value={formData.parentRoleId}
                  onChange={(e) => setFormData(prev => ({ ...prev, parentRoleId: e.target.value }))}
                  disabled={role.isSystem}
                  options={parentRoleOptions}
                />
              </div>
            </div>

            {previewRole && <RolePermissionPreview roles={[previewRole]} title="Role Preview" />}

            {/* Permissions Matrix */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Object Permissions</h3>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800 dark:text-blue-300">
                    Enabling higher-level permissions automatically enables lower levels.
                    Disabling lower-level permissions automatically disables higher levels.
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                        Object
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Read
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Write
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">
                        Delete
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {formData.permissions.map((permission, index) => (
                      <tr key={`${permission.objectId}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {permission.objectName}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StandardCheckbox
                            checked={permission.read}
                            onChange={(checked) => updatePermission(permission.objectId, 'read', checked)}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StandardCheckbox
                            checked={permission.write}
                            onChange={(checked) => updatePermission(permission.objectId, 'write', checked)}
                          />
                        </td>
                        <td className="px-4 py-3 text-center">
                          <StandardCheckbox
                            checked={permission.delete}
                            onChange={(checked) => updatePermission(permission.objectId, 'delete', checked)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Search Permissions Section */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Search className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Search Permissions</h3>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <Info className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-purple-800 dark:text-purple-300">
                    Configure what this role can search in the Global Search (⌘K). Users will only see results for categories they have permission to search.
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {/* Search Categories */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Objects */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      checked={formData.searchPermissions?.canSearchObjects ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchObjects', checked)}
                      label="Search Objects"
                      helpText="Find custom objects, clients, and records"
                    />
                  </div>

                  {/* Fields */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      checked={formData.searchPermissions?.canSearchFields ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchFields', checked)}
                      label="Search Fields"
                      helpText="Find custom fields across objects"
                    />
                  </div>

                  {/* Workflows */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      checked={formData.searchPermissions?.canSearchWorkflows ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchWorkflows', checked)}
                      label="Search Workflows"
                      helpText="Find workflow rules and automation"
                    />
                  </div>

                  {/* System Settings */}
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <StandardCheckbox
                      checked={formData.searchPermissions?.canSearchSystemSettings ?? false}
                      onChange={(checked) => updateSearchPermission('canSearchSystemSettings', checked)}
                      label="Search System Settings"
                      helpText="Find configuration pages and settings"
                    />
                  </div>
                </div>

                {/* Security Settings - Nested under System Settings */}
                {formData.searchPermissions?.canSearchSystemSettings && (
                  <div className="ml-6 pl-4 border-l-2 border-purple-300 dark:border-purple-700">
                    <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                      Security-Related Settings
                    </div>
                    <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <StandardCheckbox
                        checked={formData.searchPermissions?.canSearchSecurity ?? false}
                        onChange={(checked) => updateSearchPermission('canSearchSecurity', checked)}
                        label="Search Security Settings"
                        helpText="Find users, roles, permissions, and security configurations"
                      />
                    </div>
                  </div>
                )}

                {/* Preview Section */}
                <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Eye className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Search Preview
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {formData.searchPermissions && formData.searchPermissions.visibleCategories.length > 0 ? (
                      <>
                        This role can search: <span className="font-medium text-gray-900 dark:text-white">
                          {formData.searchPermissions.visibleCategories.join(', ')}
                        </span>
                      </>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-500">
                        No search access configured
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Delete Role Section */}
            {!role.isSystem && (
              <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Deleting this role will remove it permanently. Users with this role will need to be reassigned.
                </p>
                {!showDeleteConfirm ? (
                  <StandardButton
                    type="button"
                    variant="danger"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={Number(role.userCount) > 0}
                    title={Number(role.userCount) > 0 ? `Cannot delete role with ${Number(role.userCount)} assigned user${Number(role.userCount) > 1 ? 's' : ''}` : ''}
                  >
                    Delete Role
                  </StandardButton>
                ) : (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <div className="flex items-start gap-3 mb-4">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-red-900 dark:text-red-300 mb-1">Are you sure?</p>
                        <p className="text-sm text-red-800 dark:text-red-400">
                          This will permanently delete the role "{role.name}".
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <StandardButton
                        type="button"
                        variant="danger"
                        onClick={handleDelete}
                        isLoading={deleteRoleMutation.isPending}
                      >
                        Yes, Delete Role
                      </StandardButton>
                      <StandardButton
                        type="button"
                        variant="ghost"
                        onClick={() => setShowDeleteConfirm(false)}
                      >
                        Cancel
                      </StandardButton>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3 bg-gray-50 dark:bg-gray-900">
          <StandardButton
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </StandardButton>
          <StandardButton
            variant="primary"
            onClick={handleSubmit}
            isLoading={updateRoleMutation.isPending}
          >
            Save Changes
          </StandardButton>
        </div>
      </div>
    </div>
  );
};
